import { ForwardedRef, forwardRef, ReactElement, useMemo } from 'react';

import { TreeSelectorDummy } from '@hh.ru/magritte-ui-tree-selector/TreeSelectorDummy';
import { AdditionalDefault } from '@hh.ru/magritte-ui-tree-selector/collection/types';
import ImmutableSelectionStrategy from '@hh.ru/magritte-ui-tree-selector/strategy/immutableSelectionStrategy';
import { TreeSelectorProps, ListControls } from '@hh.ru/magritte-ui-tree-selector/types';
import { useDisabled } from '@hh.ru/magritte-ui-tree-selector/useDisabled';
import { useSelected } from '@hh.ru/magritte-ui-tree-selector/useSelected';

const TreeSelectorComponent = <Additional extends AdditionalDefault>(
    props: TreeSelectorProps<Additional>,
    ref: ForwardedRef<ListControls>
): ReactElement => {
    const {
        value,
        collection,
        singleChoice,
        leavesOnly,
        collapseToParentId,
        checkSelectable,
        onChange,
        disabled,
        maxSelected,
        ...rest
    } = props;
    const strategy = useMemo(
        () =>
            new ImmutableSelectionStrategy(collection, {
                singleChoice,
                leavesOnly,
                checkSelectable,
            }),
        [collection, singleChoice, leavesOnly, checkSelectable]
    );

    if (maxSelected && !collapseToParentId && !leavesOnly) {
        throw new Error(
            `Magritte component TreeSelector: maxSelected can be used only with collapseToParentId or leavesOnly`
        );
    }

    const { selected, setSelected } = useSelected({
        value,
        strategy,
        collection,
        onChange,
        maxSelected,
        collapseToParentId,
    });

    const currentDisabled = useDisabled({ selected, disabled, collection, maxSelected });

    return (
        <TreeSelectorDummy
            {...rest}
            value={selected}
            disabled={currentDisabled}
            collection={collection}
            singleChoice={singleChoice}
            leavesOnly={leavesOnly}
            checkSelectable={checkSelectable}
            onChange={setSelected}
            ref={ref}
        />
    );
};

export const TreeSelector = forwardRef(TreeSelectorComponent) as <Additional extends AdditionalDefault>(
    props: TreeSelectorProps<Additional> & { ref?: ForwardedRef<ListControls> }
) => ReactElement;
